import cn from 'classnames';
import type { FC } from 'react';
import { RiCheckboxBlankCircleLine, RiCheckLine, RiCloseLine, RiForbid2Line, RiTimeLine } from 'react-icons/ri';
import type { IconType } from 'react-icons';

import Tooltip from '@/components/UI/Tooltip';
import { _isNotBlank } from '@/littledash';

export const StudyStatus = {
  reviewUnsubmitted: 'review_unsubmitted',
  reviewInProgress: 'review_in_progress',
  inProgress: 'in_progress',
  completed: 'completed',
  rejected: 'rejected',
  cancelled: 'cancelled',
} as const;

export type StudyStatus = (typeof StudyStatus)[keyof typeof StudyStatus];

interface StudyStatusIndicatorProps {
  status: StudyStatus;
  date?: string;
  user?: string;
  className?: string;
}

const labels: Record<StudyStatus, string> = {
  review_unsubmitted: 'Review required',
  review_in_progress: 'Review in progress',
  in_progress: 'Study in progress',
  completed: 'Completed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

const backgrounds: Record<StudyStatus, string> = {
  review_unsubmitted: 'bg-gold',
  review_in_progress: 'bg-gold',
  in_progress: 'bg-blue',
  completed: 'bg-green',
  rejected: 'bg-red',
  cancelled: 'bg-dark-gray',
};

const textColors: Record<StudyStatus, string> = {
  review_unsubmitted: 'near-black',
  review_in_progress: 'near-black',
  in_progress: 'white',
  completed: 'white',
  rejected: 'white',
  cancelled: 'white',
};

const icons: Record<StudyStatus, IconType> = {
  review_unsubmitted: RiCheckboxBlankCircleLine,
  review_in_progress: RiCheckboxBlankCircleLine,
  in_progress: RiTimeLine,
  completed: RiCheckLine,
  rejected: RiCloseLine,
  cancelled: RiForbid2Line,
};

const tooltipFor = (status: StudyStatus, date?: string, user?: string): string => {
  switch (status) {
    case 'review_unsubmitted':
      return "This study has a review that hasn't been submitted yet.";
    case 'review_in_progress':
      return "This study has a review that hasn't been approved yet.";
    case 'in_progress':
      return 'This study is being executed.';
    case 'completed':
      return _isNotBlank(date) ? `This study was completed on ${date}.` : 'This study was completed.';
    case 'rejected':
      return _isNotBlank(user) ? `This study was rejected by ${user}.` : 'This study was rejected.';
    case 'cancelled':
      return _isNotBlank(date) ? `This study was cancelled on ${date}.` : 'This study was cancelled.';
  }
};

/**
 * Shared pill for a study's review or execution status. Hover shows the matching tooltip.
 */
const StudyStatusIndicator: FC<StudyStatusIndicatorProps> = ({ status, date, user, className }) => {
  const Glyph = icons[status];

  return (
    <Tooltip render={tooltipFor(status, date, user)}>
      <div
        className={cn(
          'inline-flex items-center flex-shrink-0 br-pill f6 fw5 ph3 pv2',
          backgrounds[status],
          textColors[status],
          className
        )}
        style={{ whiteSpace: 'nowrap' }}
        data-test-component="StudyStatusIndicator"
        data-test-element="container"
        data-testid={`study-status-${status}`}
      >
        <Glyph size={16} className="mr2" aria-hidden />
        {labels[status]}
      </div>
    </Tooltip>
  );
};

export default StudyStatusIndicator;
